import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Bar } from 'react-chartjs-2';
import StatCard from '../components/StatCard';
import MiniStatCard from '../components/MiniStatCard';
import CardSkeleton, { MiniCardSkeleton } from '../components/CardSkeleton';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import currency from 'helpers/currency';
import { Gift, DollarSign, Star, Percent } from 'react-feather';
import { Badge } from '../components/RankingTable';
import dashboardStatistics from 'api/requests/dashboardStatistics';

export default function CuponsTab({ periodParams }) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!periodParams) return;

        setIsLoading(true);
        dashboardStatistics.getCupons(periodParams)
            .then(response => setData(response.data))
            .catch(error => console.error('Erro ao carregar cupons:', error))
            .finally(() => setIsLoading(false));
    }, [periodParams]);

    if (isLoading) {
        return (
            <Container>
                <KpiGrid>
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                </KpiGrid>
                <CardSkeleton height={300} />
                <ChartsGrid>
                    <CardSkeleton height={340} />
                    <CardSkeleton height={340} />
                </ChartsGrid>
            </Container>
        );
    }

    if (!data) return null;

    const { totalAplicados, totalAplicadosTrend, totalDesconto, totalDescontoTrend, cupomMaisUsado, mediaDesconto, usoPorMes, topCupons } = data;

    const usoMesChartData = {
        labels: usoPorMes.labels,
        datasets: [
            {
                label: 'Cupons aplicados',
                data: usoPorMes.valores,
                backgroundColor: 'rgba(128, 37, 232, 0.7)',
                borderColor: '#8025e8',
                borderWidth: 1,
                borderRadius: 6,
                barPercentage: 0.6,
            },
        ],
    };

    const usoMesOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    fontColor: '#999',
                    fontSize: 12,
                },
                gridLines: { color: '#f0f0f0', drawBorder: false },
            }],
            xAxes: [{
                ticks: { fontColor: '#999', fontSize: 12 },
                gridLines: { display: false },
            }],
        },
        legend: { display: false },
        tooltips: {
            backgroundColor: '#1A1A1A',
            cornerRadius: 8,
            xPadding: 12,
            yPadding: 10,
        },
    };

    const descontoMesChartData = {
        labels: usoPorMes.labels,
        datasets: [
            {
                label: 'Descontos concedidos',
                data: usoPorMes.descontos,
                backgroundColor: 'rgba(255, 145, 35, 0.7)',
                borderColor: '#ff9123',
                borderWidth: 1,
                borderRadius: 6,
                barPercentage: 0.6,
            },
        ],
    };

    const descontoMesOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    fontColor: '#999',
                    fontSize: 12,
                    callback: function (value) {
                        if (value >= 1000) return (value / 1000) + 'k';
                        return value;
                    },
                },
                gridLines: { color: '#f0f0f0', drawBorder: false },
            }],
            xAxes: [{
                ticks: { fontColor: '#999', fontSize: 12 },
                gridLines: { display: false },
            }],
        },
        legend: { display: false },
        tooltips: {
            backgroundColor: '#1A1A1A',
            cornerRadius: 8,
            callbacks: {
                label: function (tooltipItem) {
                    return ` ${currency.format(tooltipItem.yLabel)}`;
                },
            },
        },
    };

    return (
        <Container>
            <KpiGrid>
                <MiniStatCard
                    label="Cupons Aplicados"
                    value={totalAplicados.toLocaleString('pt-BR')}
                    trend={totalAplicadosTrend}
                    isPositive
                    icon={Gift}
                />
                <MiniStatCard
                    label="Total de Desconto"
                    value={currency.format(totalDesconto)}
                    trend={totalDescontoTrend}
                    isPositive
                    icon={DollarSign}
                />
                <MiniStatCard
                    label="Cupom Mais Usado"
                    value={cupomMaisUsado}
                    icon={Star}
                />
                <MiniStatCard
                    label="Desconto Medio"
                    value={`${mediaDesconto}%`}
                    icon={Percent}
                />
            </KpiGrid>

            <TopCuponsSection>
                <StatCard title="Ranking de Cupons" subtitle="Cupons mais utilizados">
                    {topCupons.length > 0 ? (
                        <>
                            <CuponsHeader>
                                <CuponsHeaderItem flex={2}>Cupom</CuponsHeaderItem>
                                <CuponsHeaderItem>Usos</CuponsHeaderItem>
                                <CuponsHeaderItem>Desconto Total</CuponsHeaderItem>
                                <CuponsHeaderItem align="right">% Desconto</CuponsHeaderItem>
                            </CuponsHeader>
                            {topCupons.map((cupom, index) => (
                                <CupomItem key={index}>
                                    <CupomCode flex={2}>
                                        <RankBadge>#{index + 1}</RankBadge>
                                        <code>{cupom.codigo}</code>
                                    </CupomCode>
                                    <CupomValue>{cupom.usos.toLocaleString('pt-BR')}</CupomValue>
                                    <CupomValue>{currency.format(cupom.desconto)}</CupomValue>
                                    <CupomPercent>
                                        <Badge bg="#FFF3E0" color="#ff9123">{cupom.percentualDesconto}</Badge>
                                    </CupomPercent>
                                </CupomItem>
                            ))}
                        </>
                    ) : (
                        <EmptyMessage>Nenhum cupom utilizado no periodo.</EmptyMessage>
                    )}
                </StatCard>
            </TopCuponsSection>

            <ChartsGrid>
                <StatCard title="Cupons Aplicados por Mes" subtitle="Uso mensal de cupons promocionais">
                    <ChartContainer>
                        <Bar data={usoMesChartData} options={usoMesOptions} />
                    </ChartContainer>
                </StatCard>

                <StatCard title="Descontos Concedidos por Mes" subtitle="Valor total de descontos mensais">
                    <ChartContainer>
                        <Bar data={descontoMesChartData} options={descontoMesOptions} />
                    </ChartContainer>
                </StatCard>
            </ChartsGrid>
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const KpiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media screen and (max-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr;
    }
`;

const TopCuponsSection = styled.div``;

const EmptyMessage = styled.p`
    font-size: 14px;
    color: ${colors.night};
    margin: 0;
    padding: 12px 0;
`;

const CuponsHeader = styled.div`
    display: flex;
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 4px;
`;

const CuponsHeaderItem = styled.div`
    flex: ${({ flex }) => flex || 1};
    font-size: 12px;
    color: ${colors.night};
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: ${({ align }) => align || 'left'};
`;

const CupomItem = styled.div`
    display: flex;
    align-items: center;
    padding: 14px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
        border-bottom: none;
    }
`;

const CupomCode = styled.div`
    flex: ${({ flex }) => flex || 1};
    display: flex;
    align-items: center;
    gap: 10px;

    code {
        font-size: 14px;
        font-weight: 600;
        color: ${colors.nightDark};
        background: #f5f5f5;
        padding: 4px 8px;
        border-radius: 4px;
    }
`;

const RankBadge = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: #F3E8FF;
    color: ${colors.secondaryPurple};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 12px;
    flex-shrink: 0;
`;

const CupomValue = styled.div`
    flex: 1;
    font-size: 14px;
    color: ${colors.nightDark};
    font-weight: 500;
`;

const CupomPercent = styled.div`
    flex: 1;
    text-align: right;
`;

const ChartsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    @media screen and (max-width: ${breakpoints.md}) {
        grid-template-columns: 1fr;
    }
`;

const ChartContainer = styled.div`
    height: 280px;
    width: 100%;
`;
